import {
  Button,
  Filters,
  Header,
  Search,
  Select,
  StickyFooterButtons,
  useBreakpoints,
  useLoading,
} from "best-common-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { getRequestsBySeriesNumber } from "../../../api/RequesTixApi";
import AdminRequestGame from "../../../components/requests/admin/AdminRequestGame";
import RouteConstants from "../../../constants/RouteConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { AdminRequestsDTO } from "../../../types/AdminRequest";
import { getCategoryOption, getCompleteOption, getStatusOption } from "../../../util/DropdownUtil";
import { useUrlParams } from "../../../util/RouteUtil";
import { requestsFreeTextFilter } from "../../../util/TicketRequestUtil";

const defaultData: AdminRequestsDTO = {
  schedule: [],
  requests: {},
  games: {},
  totalRequests: {},
  gameList: [],
  ticketRequests: [],
};

type AdminSuiteRequestEditUrlProps = {
  homeTeamId?: string;
  seriesNumber?: string;
  season?: string;
  gameType?: string;
};

const AdminRequestsBySeries = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();
  const { homeTeamId, seriesNumber, season, gameType } = useParams<AdminSuiteRequestEditUrlProps>();
  const { setLoading } = useLoading();
  const { requestCategoryOptions, requestStateOptions, completeOptions } = useDropdowns();
  const [data, setData] = useState<AdminRequestsDTO>(defaultData);
  const [filteredData, setFilteredData] = useState({});

  const [{ statusId, categoryId, completeId, search }, setParam] = useUrlParams({
    statusId: undefined,
    categoryId: undefined,
    completeId: undefined,
    search: undefined,
  });

  const category = useMemo(
    () => getCategoryOption(categoryId ?? "", requestCategoryOptions),
    [categoryId, requestCategoryOptions],
  );
  const status = useMemo(() => getStatusOption(statusId ?? "", requestStateOptions), [statusId, requestStateOptions]);
  const complete = useMemo(() => getCompleteOption(completeId ?? "", completeOptions), [completeId, completeOptions]);

  const close = useCallback(() => {
    if (location?.state?.previousLocation) {
      navigate(location.state.previousLocation);
    } else {
      navigate(RouteConstants.ADMIN.REQUESTS);
    }
  }, [location, navigate]);

  useEffect(() => {
    setLoading(true);
    getRequestsBySeriesNumber(
      season!,
      homeTeamId!,
      seriesNumber!,
      gameType!,
      //@ts-ignore
      statusId ?? "",
      categoryId ?? "",
      completeId ?? "",
    )
      .then((resp: AdminRequestsDTO) => {
        setData(resp);
        setLoading(false);
      })
      .catch(() => {
        setData(defaultData);
      });
  }, [homeTeamId, seriesNumber, season, statusId, categoryId, completeId]);

  useEffect(() => {
    setFilteredData(requestsFreeTextFilter(data.requests, search ?? ""));
  }, [data, search]);

  return (
    <div>
      <Header title="Ticket Requests" flexColumn>
        <Filters actionIconPosition="absolute" numberDisplayed={isMobile ? 1 : 4}>
          <Select
            id="status"
            label="Status"
            value={status}
            options={requestStateOptions}
            onChange={(value) =>
              setParam("statusId", value?.value?.requestStateId ? String(value.value.requestStateId) : undefined)
            }
            placeholder="select a status"
          />
          <Select
            id="complete"
            label="Complete/Incomplete"
            value={complete}
            options={completeOptions}
            onChange={(value) => setParam("completeId", value?.value?.id ? String(value.value.id) : undefined)}
            placeholder="select a complete status"
          />
          <Select
            id="category"
            label="Category"
            value={category}
            options={requestCategoryOptions}
            onChange={(value) =>
              setParam(
                "categoryId",
                value?.value?.requestCategoryId ? String(value.value.requestCategoryId) : undefined,
              )
            }
            placeholder="select a category"
          />
          <Search
            id="search"
            label="Search Requests"
            value={search}
            onChange={(value: string) => setParam("search", value || undefined)}
            placeholder="begin typing to filter requests"
          />
        </Filters>
      </Header>
      <div className="bcr-p-2">
        <div>
          {data.schedule.map((gamePk) => (
            <AdminRequestGame key={gamePk} game={data.games[gamePk]} requests={filteredData[gamePk]} />
          ))}
        </div>
        <StickyFooterButtons startsOpen={true}>
          <Button variant="default" onClick={close}>
            Close
          </Button>
        </StickyFooterButtons>
      </div>
    </div>
  );
};

export default AdminRequestsBySeries;
